import { defineStore } from "pinia";
import { usePersonalInfoStore } from "./PersonalInfoStore";
import { usePlanStore } from "./PlanStore";
import { usePlanPriceChangeReservationStore } from "./PlanPriceChangeReservationStore";
import { useLoginStore } from "./LoginStore";
import { usePayment3DSStore } from "./Payment3DSStore";
import { PlanInfo } from "~/entities/PlanInfo";
import { type IContractMemberRepository } from "~/interfaces/IContractMemberRepository";
import { type IPaymentRepository } from "~/interfaces/IPaymentRepository";
import { type IPlanRepository } from "~/interfaces/IPlanRepository";
import { type IPlanPriceChangeReservationRepository } from "~/interfaces/IPlanPriceChangeReservationRepository";
import { ContractMemberRepository } from "~/Repositories/ContractMemberRepository";
import { PaymentRepository } from "~/Repositories/PaymentRepository";
import { PlanRepository } from "~/Repositories/PlanRepository";
import { PlanPriceChangeReservationRepository } from "~/Repositories/PlanPriceChangeReservationRepository";
import { PlanDocument } from "~/entities/PlanDocument";

export type PlanJoinStatus =
  | "agreement"
  | "personalinfo"
  | "documents"
  | "payment-method"
  | "payment-card"
  | "payment-bank"
  | "confirm";

export const usePlanJoinStore = defineStore("PlanJoinStore", () => {
  // stores
  const personalInfoStore = usePersonalInfoStore();
  const planStore = usePlanStore();
  const planPriceChangeReservationStore = usePlanPriceChangeReservationStore();
  const loginStore = useLoginStore();
  const payment3DSStore = usePayment3DSStore();

  // repositories
  const planRepo: IPlanRepository = new PlanRepository();
  const paymentRepo: IPaymentRepository = new PaymentRepository();
  const contraceMemberRepo: IContractMemberRepository = new ContractMemberRepository();
  const planPriceChangeReservationRepo: IPlanPriceChangeReservationRepository =
    new PlanPriceChangeReservationRepository();

  // states
  const plan = ref<PlanInfo | undefined>(undefined);
  const priceId = ref<string | undefined>(undefined);
  const documents = ref<PlanDocument[]>([]);
  const flow = ref<PlanJoinStatus[]>([]);
  const hasAgreedConditions = ref(false);
  const hasAgreedDocuments = ref(false);
  const sbPaymentId = ref<number | undefined>(undefined);
  const addedSbPaymentId = ref<number | undefined>(undefined);
  const bankTransferId = ref<number | undefined>(undefined);
  const divideTimes = ref<number | undefined>(undefined);
  const creditResultId = ref<number | undefined>(undefined);
  const inputEmail = ref<string>("");

  const reset = () => {
    plan.value = undefined;
    priceId.value = undefined;
    documents.value = [];
    flow.value = [];
    hasAgreedConditions.value = false;
    hasAgreedDocuments.value = false;
    sbPaymentId.value = undefined;
    addedSbPaymentId.value = undefined;
    bankTransferId.value = undefined;
    divideTimes.value = undefined;
    creditResultId.value = undefined;
    inputEmail.value = "";
  };

  const isFreePlan = computed(() => {
    return plan.value?.planPriceList.find(p => p.planPriceId.toString() === priceId.value)?.price === 0;
  });

  const hasFreeTrail = computed(() => {
    return (plan.value?.planPriceList.find(p => p.planPriceId.toString() === priceId.value)?.trialDays || 0) > 0;
  });

  const isBillingOnlyOnce = computed(() => {
    return plan.value?.planPriceList.find(p => p.planPriceId.toString() === priceId.value)?.billingType === "only_once";
  });

  const planPrice = computed(() => {
    return plan.value?.planPriceList.find(p => p.planPriceId.toString() === priceId.value);
  });

  const init = async (planId: string, planPriceId: string, cancelPlanPriceId?: string, isChange?: boolean) => {
    // まずは状態初期化
    reset();

    // 講座情報を取得
    await planStore.fetchOne(planId);
    if (!planStore.plan) {
      throw new Error("plan not found");
    }
    plan.value = planStore.plan;

    // 料金プラン予約情報を取得
    await planPriceChangeReservationStore.fetch(planId);

    // planPriceListに指定のplanPriceIdが存在しない場合はエラー
    if (!plan.value.planPriceList.some(planPrice => planPrice.planPriceId === Number(planPriceId))) {
      throw new Error("planPrice not found");
    }

    // 料金IDをセット
    priceId.value = planPriceId;

    const hasCancelPlanPriceId =
      !!cancelPlanPriceId &&
      plan.value.planPriceList.some(planPrice => planPrice.planPriceId === Number(cancelPlanPriceId));
    // 既に契約済み
    if (planStore.plan.isContract && !hasCancelPlanPriceId && !isChange) {
      if (featureChangePrice() && planStore.plan.contractStatus.planPriceId !== Number(planPriceId)) {
        // 現在のURLのqueryにisChange=trueを追加してリロード
        const route = useRoute();
        const newQuery = { ...route.query, isChange: "true" };
        // クエリパラメータを含めたURLを作成
        const fullUrl = `${route.path}?${new URLSearchParams(newQuery).toString()}`;
        // リロードを実行
        await reloadNuxtApp({ path: fullUrl, ttl: 1000 }); // デフォルト 10,000
        return false;
      }
      return false;
    }

    // 投資助言あり講座であれば交付文書を取得
    if (planStore.plan.isInvestmentAdvice) {
      documents.value = await planRepo.fetchDocuments(planId);
    }

    if (loginStore.isLogin()) {
      // メールアドレス認証状態を使ってフロー構築
      await personalInfoStore.fetchIsEmailVerified();
    }
    initFlow(plan.value, personalInfoStore.isEmailVerified, isChange);

    return true; // init 成功
  };

  const reinitIfNeeded = async (
    planId: string,
    planPriceId: string,
    cancelPlanPriceId?: string,
    isChange?: boolean,
  ) => {
    // 3Dセキュア認証でPlanJoinStoreのデータを保存している場合は、sessionStorageからデータを復元
    payment3DSStore.restorePlanJoinStoreData(planId, planPriceId);

    if (flow.value.length > 0) {
      return true; // フロー構築済みであれば何もしない
    }
    // planPriceIdが設定されていない場合は初期化
    if (!planPriceId) {
      await reloadNuxtApp({ path: "/", ttl: 1000 }); // デフォルト 10,000
      return false;
    }

    try {
      if (await init(planId, planPriceId, cancelPlanPriceId, isChange)) {
        // 現在のパスがflowに含まれていない場合は最初のflowにリダイレクト
        const route = useRoute();
        const lastSegment = route.path.split("/").pop();
        if (featureChangePrice() && !flow.value.includes(lastSegment as PlanJoinStatus)) {
          const newQuery = { ...(route.query as Record<string, string>) };
          // クエリパラメータを含めたURLを作成
          const fullUrl = `/plan/${planId}/join/${flow.value[0]}?${new URLSearchParams(newQuery).toString()}`;
          // リロードを実行
          await reloadNuxtApp({ path: fullUrl, ttl: 1000 }); // デフォルト 10,000
          return false;
        } else {
          return true;
        }
      }
    } catch (error: any) {
      // エラーが発生した場合はトップページにリダイレクト
      reloadNuxtApp({ path: "/", ttl: 1000 }); // デフォルト 10,000
    }
    return true;
  };

  const initFlow = (planInfo: PlanInfo, isEmailVerified: boolean, isChange?: boolean) => {
    flow.value = [];
    // 講座入会フローを初期化
    if (!isChange) {
      // 条件同意はフラグをみて実施する
      if (planInfo.isHiddenAgreementTerms) {
        // 契約同意画面非表示フラグがtrueの場合は条件同意フラグをtrueにする
        hasAgreedConditions.value = true;
      } else {
        flow.value.push("agreement");
      }

      // 次の個人情報登録も必ず実施
      // https://invinc.slack.com/archives/C04HG0404HJ/p1706684390908479?thread_ts=1706682031.523899&cid=C04HG0404HJ
      // 非助言講座の場合は個人情報登録をスキップ
      if (planInfo.personalInfoType !== "OPTIONAL_B") {
        flow.value.push("personalinfo");
      }
    }

    // 投資助言あり講座であれば書面確認する
    if (planInfo.isInvestmentAdvice && !isChange) {
      flow.value.push("documents");
    }

    // 無料講座でなければ決済方法を選択する
    if (!isFreePlan.value) {
      // 一括決済の場合はクレカ or 銀行振込を選択
      if (isBillingOnlyOnce.value) {
        flow.value.push("payment-method");
      }
      flow.value.push("payment-card");
    } else {
      // 無料の場合
      flow.value.push("confirm");
    }
  };

  const firstStatus = () => {
    return flow.value[0];
  };

  const updatePaymentFlow = (value: "card" | "bank") => {
    bankTransferId.value = undefined;
    sbPaymentId.value = undefined;
    flow.value = flow.value.map((item: PlanJoinStatus) => {
      if ((item.includes("-bank") || item.includes("-card")) && item !== `payment-${value}`) {
        return `payment-${value}` as PlanJoinStatus;
      }
      return item;
    });
  };

  const getPaymentFlow = (): "card" | "bank" => {
    const paymentMethod = flow.value.find(item => item.includes("-bank") || item.includes("-card"));
    if (paymentMethod) {
      return paymentMethod.split("-")[1] as "card" | "bank";
    }
    return "card";
  };

  const nextStatusOf = (currentStatus: PlanJoinStatus) => {
    const index = flow.value.findIndex(s => s === currentStatus);
    if (index === -1) {
      return undefined;
    }
    if (index + 1 >= flow.value.length) {
      return undefined;
    }
    return flow.value[index + 1];
  };

  const isStatusValid = (status: PlanJoinStatus) => {
    // 講座未設定または契約済みの場合は入会できない
    if (!plan.value || plan.value?.isContract) {
      return false;
    }
    switch (status) {
      case "agreement":
        // 無条件でOK
        return true;
      case "personalinfo":
      case "documents":
        // 条件同意必須
        return hasAgreedConditions.value;
      case "payment-method":
      case "payment-card":
      case "payment-bank":
      case "confirm":
        // 条件同意必須
        // 投資助言ありの場合は書面同意必須
        return hasAgreedConditions.value && (!plan.value.isInvestmentAdvice || hasAgreedDocuments.value);
      default:
        throw new Error("invalid status");
    }
  };

  const join = async (
    planId: string,
    planPriceId: string,
    cancelPlanPriceId?: string,
    isChange?: boolean,
    compaignId?: string,
  ) => {
    if (hasFreeTrail.value) {
      if (isChange) {
        // 変更の場合の処理
        await planPriceChangeReservationRepo.set(planPriceId, sbPaymentId.value ?? null);
      } else if (!cancelPlanPriceId) {
        // 有料講座でトライアル期間がある場合は契約のみで入会
        await contraceMemberRepo.contractWithoutPayment(String(sbPaymentId.value!), planPriceId);
      } else {
        // 有料講座でトライアル期間がある場合は契約のみで入会,  ワンタイムオファーあり
        await contraceMemberRepo.contractWithoutPaymentUpdate(String(sbPaymentId.value!), planPriceId, compaignId);
      }
    } else if (!isFreePlan.value) {
      // 有料講座の場合
      if (isChange) {
        // 変更の場合の処理
        await planPriceChangeReservationRepo.set(planPriceId, sbPaymentId.value ?? null);
      } else if (bankTransferId.value) {
        // 振込の場合は契約のみで入会
        await paymentRepo.bankTransfer(bankTransferId.value, planPriceId);
      } else {
        // 通常入会でクレジットカードの場合は決済して入会
        const creditResultId = await paymentRepo.prepareSettlement(
          sbPaymentId.value!,
          planPriceId,
          divideTimes.value!,
          cancelPlanPriceId,
        );
        await paymentRepo.settlement(creditResultId, false, cancelPlanPriceId, compaignId);
      }
    } else {
      // 無料講座の場合は契約のみで入会
      await contraceMemberRepo.contract(planId);
    }
  };

  const contractMemberCancelRequest = async (planId: string) => {
    await contraceMemberRepo.contractMemberCancelRequest(planId);
  };

  return {
    plan,
    planPrice,
    priceId,
    documents,
    flow,
    hasAgreedConditions,
    hasAgreedDocuments,
    sbPaymentId,
    addedSbPaymentId,
    bankTransferId,
    divideTimes,
    creditResultId,
    inputEmail,
    reset,
    init,
    updatePaymentFlow,
    reinitIfNeeded,
    firstStatus,
    nextStatusOf,
    isStatusValid,
    join,
    getPaymentFlow,
    contractMemberCancelRequest,
  };
});
